import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, MessageSquareReply, Briefcase, Building2, Star, ChevronRight } from 'lucide-react';
import api from '@/utils/api';

const Card = ({ id, icon: Icon, title, count, sub, tone, onOpen, children }) => (
  <div className={`rounded-2xl border bg-white p-4 flex flex-col ${count > 0 ? tone : 'border-slate-200'}`} data-testid={`attention-card-${id}`}>
    <button type="button" onClick={onOpen} className="flex items-start gap-3 text-left group" data-testid={`attention-open-${id}`}>
      <span className="rounded-xl bg-slate-100 p-2"><Icon className="h-5 w-5 text-slate-700" /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</span>
        <span className="block text-3xl font-black text-slate-900 tabular-nums" data-testid={`attention-count-${id}`}>{count}</span>
        {sub && <span className="block text-xs text-slate-500">{sub}</span>}
      </span>
      <ChevronRight className="h-4 w-4 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
    </button>
    {children}
  </div>
);

const Items = ({ items, onPick, testid }) => (items?.length ? (
  <ul className="mt-3 space-y-1 border-t border-slate-100 pt-2">
    {items.slice(0, 4).map((it) => (
      <li key={it.id}><button type="button" onClick={() => onPick(it)} className="w-full truncate text-left text-xs text-blue-700 hover:underline" data-testid={`${testid}-${it.id}`}>{it.label ? `${it.label} · ` : ''}{it.company}</button></li>
    ))}
  </ul>
) : null);

export const AttentionToday = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/admin/attention').then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === false) return <section className="mb-8" data-testid="attention-today"><h2 className="mb-2 text-lg font-bold text-slate-900">Precisa de atenção hoje</h2><p className="text-sm text-red-600" data-testid="attention-error">Não foi possível carregar os alertas. Actualize a página.</p></section>;
  return (
    <section className="mb-8" data-testid="attention-today">
      <h2 className="mb-3 text-lg font-bold text-slate-900">Precisa de atenção hoje</h2>
      {!d ? <p className="text-sm text-slate-500">A carregar…</p> : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <Card id="meetings" icon={CalendarDays} title="Reuniões hoje e amanhã" count={d.meetings.count} tone="border-blue-300" onOpen={() => navigate('/admin/prospects/agenda')}>
            <Items items={d.meetings.items} onPick={() => navigate('/admin/prospects/agenda')} testid="attention-meeting" />
          </Card>
          <Card id="unanswered" icon={MessageSquareReply} title="Responderam, sem resposta" count={d.unanswered_prospects.count} tone="border-rose-300" onOpen={() => navigate(d.unanswered_prospects.items[0] ? `/admin/prospects?pid=${d.unanswered_prospects.items[0].id}` : '/admin/prospects')}>
            <Items items={d.unanswered_prospects.items} onPick={(it) => navigate(`/admin/prospects?pid=${it.id}`)} testid="attention-unanswered" />
          </Card>
          <Card id="jobs" icon={Briefcase} title="Vagas a verificar" count={d.jobs.count} sub={`${d.jobs.pending_review} à espera · ${d.jobs.suspicious} suspeitas`} tone="border-orange-300"
            onOpen={() => navigate(`/admin/jobs?tab=${d.jobs.pending_review ? 'pending_review' : 'suspicious'}`)} />
          <Card id="companies" icon={Building2} title="Empresas novas (7 dias)" count={d.new_companies.count} tone="border-emerald-300" onOpen={() => navigate('/admin/companies?filter=new7')}>
            <Items items={d.new_companies.items} onPick={(it) => navigate(`/admin/companies?search=${encodeURIComponent(it.company || '')}`)} testid="attention-company" />
          </Card>
          <Card id="pioneers" icon={Star} title="Pioneiros no Mural" count={d.pioneers_mural.count} sub={`${d.pioneers_mural.awaiting_logo} sem logo · ${d.pioneers_mural.awaiting_approval} por aprovar`} tone="border-amber-300" onOpen={() => navigate('/admin/trusted')} />
        </div>
      )}
    </section>
  );
};
